import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { GoogleButton } from '@/components/GoogleButton';
import { useToast } from '@/components/toast';
import { ErrorText } from '@/components/ui';
import { authClient, authErrorMessage } from '@/lib/auth-client';
import { keys, useMe } from '@/lib/queries';
import { AuthLayout } from './AuthLayout';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const me = useMe();
  const toast = useToast();
  const state = location.state as { from?: string; toast?: string } | null;
  const from = state?.from ?? '/app';
  useEffect(() => {
    if (state?.toast) toast(state.toast);
  }, [state?.toast, toast]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await authClient.signIn.email({ email, password });
    if (res.error) {
      setError(authErrorMessage(res.error, 'Wrong email or password'));
      setBusy(false);
      return;
    }
    await qc.invalidateQueries({ queryKey: keys.me });
    void navigate(from, { replace: true });
  };

  return (
    <AuthLayout title="Welcome back." sub="Log in to your posts, calendar and media.">
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <GoogleButton enabled={!!me.data?.features.googleAuth} callbackURL="/app" />
        <div className="divider">or</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            className="input"
            type="email"
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Email"
            required
          />
          <input
            className="input"
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-label="Password"
            required
          />
        </div>
        <ErrorText>{error}</ErrorText>
        <button type="submit" className="btn btn-primary btn-lg hv" disabled={busy}>
          {busy ? 'Logging in…' : 'Log in'}
        </button>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 12.5,
            color: '#777',
          }}
        >
          <Link to="/forgot-password" className="tap-link">
            Forgot password?
          </Link>
          <span>
            New here?{' '}
            <Link to="/" className="tap-link" style={{ fontWeight: 600 }}>
              Paste your listing
            </Link>
          </span>
        </div>
      </form>
    </AuthLayout>
  );
}
